import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getApiErrorMessage } from '../../api';
import { tripService, Trip } from '../../api/trips';
import { employeeService, type Employee } from '../../api/Employee';
import { vehicleService, type Vehicle } from '../../api/vehicles';
import {
  formatDatePtBR,
  formatDateTimePtBR,
  formatTimePtBR,
} from '../../utils/formatters';
import {
  Route,
  Search,
  RefreshCw,
  Plus,
  MapPin,
  Calendar,
  Clock,
  Car,
  User,
  FileText,
  Eye,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  X,
} from 'lucide-react';
import {
  PageHeader,
  Button,
  Badge,
  Input,
  Select,
  Textarea,
  Label,
  Modal,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  ConfirmModal,
} from '../../components/ui/SharedLayout';

const ITEMS_PER_PAGE = 20;

const statusLabelMap: Record<string, string> = {
  pendente: 'Pendente',
  aprovada: 'Aprovada',
  rejeitada: 'Rejeitada',
  em_andamento: 'Em andamento',
  concluida: 'Concluída',
  cancelada: 'Cancelada',
};

const SolicitacaoViagem = () => {
  const { setPageTitle } = useAuth();
  const [solicitacoes, setSolicitacoes] = useState<Trip[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [vehicleId, setVehicleId] = useState<number | null>(null);
  const [employeeId, setEmployeeId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [destination, setDestination] = useState('');
  const [purpose, setPurpose] = useState('');

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [solicitacaoSelecionada, setSolicitacaoSelecionada] = useState<Trip | null>(null);

  const [rejectionReason, setRejectionReason] = useState('');
  const [confirmData, setConfirmData] = useState<{
    isOpen: boolean;
    title: string;
    description?: string | React.ReactNode;
    variant?: 'danger' | 'primary' | 'success';
    confirmText?: string;
    cancelText?: string;
    onConfirm?: () => Promise<void> | void;
  }>({ isOpen: false, title: '' });

  useEffect(() => {
    setPageTitle('Solicitações de viagem do setor');
  }, [setPageTitle]);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 2800);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [trips, scopedVehicles, scopedEmployees] = await Promise.all([
        tripService.getAll(),
        vehicleService.getAllVehicles(),
        employeeService.getAllEmployees(),
      ]);
      setSolicitacoes(trips || []);
      setVehicles(scopedVehicles || []);
      setEmployees(scopedEmployees || []);
    } catch (err) {
      setSolicitacoes([]);
      setVehicles([]);
      setEmployees([]);
      setError(getApiErrorMessage(err, 'Erro ao carregar solicitações de viagem.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const filteredSolicitacoes = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return solicitacoes
      .filter((s) => s.status !== 'concluida')
      .filter((s) => {
        if (!term) return true;
        return [
          s.destination || '',
          s.purpose || '',
          s.vehicle?.brand || '',
          s.vehicle?.license_plate || '',
          s.vehicle?.description || '',
          s.employee?.name || '',
          s.employee?.cpf || '',
        ]
          .join(' ')
          .toLowerCase()
          .includes(term);
      });
  }, [searchTerm, solicitacoes]);

  const pendingCount = useMemo(
    () => filteredSolicitacoes.filter((s) => s.status === 'pendente').length,
    [filteredSolicitacoes],
  );

  const totalPages = Math.max(1, Math.ceil(filteredSolicitacoes.length / ITEMS_PER_PAGE));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [filteredSolicitacoes.length, currentPage, totalPages]);

  const { solicitacoesPaginadas, pageRange } = useMemo(() => {
    const total = filteredSolicitacoes.length;
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return {
      solicitacoesPaginadas: filteredSolicitacoes.slice(startIndex, endIndex),
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [filteredSolicitacoes, currentPage, totalPages]);

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPages - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    pages.push(totalPages);
    return pages;
  }, [currentPage, totalPages]);

  const formatStatus = (status?: string) => statusLabelMap[status ?? ''] ?? status ?? '-';

  const renderStatusBadge = (status?: string) => {
    switch (status) {
      case 'pendente':
        return (
          <Badge tone="amber" className="gap-1.5">
            <Clock className="h-3 w-3 stroke-[2]" />
            Pendente
          </Badge>
        );
      case 'aprovada':
        return (
          <Badge tone="emerald" className="gap-1.5">
            <CheckCircle2 className="h-3 w-3 stroke-[2]" />
            Aprovada
          </Badge>
        );
      case 'rejeitada':
        return (
          <Badge tone="red" className="gap-1.5">
            <XCircle className="h-3 w-3 stroke-[2]" />
            Rejeitada
          </Badge>
        );
      case 'em_andamento':
        return (
          <Badge tone="blue" className="gap-1.5">
            <Route className="h-3 w-3 stroke-[2]" />
            Em andamento
          </Badge>
        );
      case 'cancelada':
        return (
          <Badge tone="slate" className="gap-1.5">
            <X className="h-3 w-3 stroke-[2]" />
            Cancelada
          </Badge>
        );
      case 'concluida':
      default:
        return (
          <Badge tone="slate" className="gap-1.5">
            {formatStatus(status)}
          </Badge>
        );
    }
  };

  const formatDate = (dateStr?: string) => formatDatePtBR(dateStr);

  const formatTime = (dateStr?: string) => formatTimePtBR(dateStr);

  const formatDateTime = (dateStr?: string) => formatDateTimePtBR(dateStr);

  const resetCreateForm = () => {
    setVehicleId(null);
    setEmployeeId(null);
    setStartDate('');
    setEndDate('');
    setDestination('');
    setPurpose('');
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    setSubmitting(false);
    resetCreateForm();
  };

  const submitCreateTrip = async (event: React.FormEvent) => {
    event.preventDefault();
    if (
      !vehicleId ||
      !employeeId ||
      !startDate ||
      Number.isNaN(Number(vehicleId)) ||
      Number.isNaN(Number(employeeId)) ||
      Number.isNaN(Date.parse(startDate))
    ) {
      setToast({ type: 'error', message: 'Preencha todos os campos obrigatórios corretamente.' });
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await tripService.create({
        vehicle_id: Number(vehicleId),
        employee_id: Number(employeeId),
        start_date: new Date(startDate).toISOString(),
        end_date: endDate ? new Date(endDate).toISOString() : undefined,
        purpose: purpose.trim() || undefined,
        destination: destination.trim() || undefined,
        status: 'pendente',
      });
      await fetchData();
      closeCreateModal();
      setToast({ type: 'success', message: 'Solicitação enviada com sucesso!' });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao enviar solicitação de viagem.'));
      setToast({ type: 'error', message: 'Erro ao enviar solicitação.' });
      setSubmitting(false);
    }
  };

  const visualizarSolicitacao = (solicitacao: Trip) => {
    setSolicitacaoSelecionada(solicitacao);
    setIsViewModalOpen(true);
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setSolicitacaoSelecionada(null);
  };

  const handleAprovar = (trip: Trip) => {
    setConfirmData({
      isOpen: true,
      title: 'Confirmar Aprovação',
      description: `Deseja aprovar a viagem para ${trip.destination || 'destino não informado'}?`,
      confirmText: 'Confirmar Aprovação',
      cancelText: 'Cancelar',
      variant: 'success',
      onConfirm: async () => {
        try {
          if (trip.status !== 'pendente') {
            setToast({
              type: 'error',
              message: `Não é possível aprovar uma viagem com status "${formatStatus(trip.status)}". Apenas pendentes podem ser aprovadas.`,
            });
            return;
          }
          const updatedTrip = await tripService.approve(trip.id);
          setSolicitacoes((curr) => curr.map((t) => (t.id === trip.id ? updatedTrip : t)));
          if (solicitacaoSelecionada?.id === updatedTrip.id) {
            setSolicitacaoSelecionada(updatedTrip);
          }
          setToast({ type: 'success', message: 'Viagem aprovada com sucesso!' });
        } catch (_err) {
          setToast({ type: 'error', message: 'Erro ao aprovar a viagem.' });
        } finally {
          setConfirmData((p) => ({ ...p, isOpen: false }));
        }
      },
    });
  };

  const handleRejeitar = (trip: Trip) => {
    setRejectionReason('');
    setConfirmData({
      isOpen: true,
      title: 'Rejeitar Viagem',
      variant: 'danger',
      confirmText: 'Confirmar Rejeição',
      cancelText: 'Cancelar',
      description: (
        <div className="space-y-3">
          <p className="text-sm text-slate-600">Informe o motivo da rejeição:</p>
          <Textarea
            className="min-h-[110px]"
            placeholder="Motivo da rejeição (obrigatório)"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
          />
        </div>
      ),
      onConfirm: async () => {
        if (!rejectionReason.trim()) {
          setToast({ type: 'error', message: 'Informe o motivo da rejeição.' });
          return;
        }
        try {
          const updatedTrip = await tripService.reject(trip.id, rejectionReason.trim());
          setSolicitacoes((curr) => curr.map((t) => (t.id === trip.id ? updatedTrip : t)));
          if (solicitacaoSelecionada?.id === trip.id) {
            setSolicitacaoSelecionada(updatedTrip);
          }
          setRejectionReason('');
          setToast({ type: 'success', message: 'Viagem rejeitada com sucesso!' });
        } catch (_err) {
          setToast({ type: 'error', message: 'Erro ao rejeitar a viagem.' });
        } finally {
          setConfirmData((p) => ({ ...p, isOpen: false }));
        }
      },
    });
  };

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        <PageHeader title="Solicitações de viagem do setor" hideTitleInBody />

        {toast && (
          <div
            className={`fixed top-5 right-5 z-[100] inline-flex items-center gap-2.5 rounded-xl px-4 py-3 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.35)] border animate-[fadeIn_0.2s_ease-out] ${
              toast.type === 'success'
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : 'bg-rose-50 border-rose-200 text-rose-800'
            }`}
          >
            {toast.type === 'success' ? (
              <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
            ) : (
              <XCircle className="h-5 w-5 text-rose-600 shrink-0" />
            )}
            <span className="text-[13px] font-semibold leading-tight">{toast.message}</span>
          </div>
        )}

        <div className="mb-7 flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1.5 max-w-2xl">
            <p className="text-[15px] font-semibold leading-tight text-sky-600">
              Operações — Solicitações de Viagem
            </p>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <h1 className="text-[22px] sm:text-[26px] font-bold tracking-tight leading-tight text-slate-900">
                Crie, aprove ou rejeite solicitações
              </h1>
              <span className="text-[18px] sm:text-[20px] font-medium text-slate-500 leading-tight">
                de viagem da sua equipe
              </span>
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-2 w-full sm:w-auto mt-1 sm:mt-0">
            {pendingCount > 0 && (
              <div className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 text-[13px] font-bold text-amber-700">
                <Clock className="h-4 w-4 stroke-[1.9]" />
                Pendentes: {pendingCount}
              </div>
            )}
            <Button variant="secondary" onClick={() => void fetchData()} className="h-10 px-4">
              <RefreshCw className="h-4 w-4 stroke-[1.8]" />
              Atualizar
            </Button>
            <Button variant="primary" onClick={() => setIsCreateModalOpen(true)} className="h-10 px-5">
              <Plus className="h-4 w-4 stroke-[1.9]" />
              Nova Solicitação
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 inline-flex items-start gap-3 w-full">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
            <span>{error}</span>
          </div>
        )}

        <SectionCard>
          <CardHeader>
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 min-w-[44px] min-h-[44px] shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 via-blue-500 to-sky-600 text-white shadow-[0_8px_22px_-10px_rgba(79,70,229,0.7)]">
                  <Route className="h-[22px] w-[22px] stroke-[1.9]" />
                </div>
                <div>
                  <h2 className="text-[15px] font-bold tracking-tight text-slate-900">
                    Solicitações em aberto
                  </h2>
                  <p className="mt-0.5 text-[12.5px] font-medium leading-tight text-slate-500">
                    Pendentes, aprovadas, rejeitadas e em andamento do seu departamento
                  </p>
                </div>
              </div>
              <div className="relative w-full sm:w-[360px] shrink-0">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Buscar destino, veículo, motorista..."
                  className="!pl-10 h-10"
                />
              </div>
            </div>
          </CardHeader>

          <CardBody>
            {loading && solicitacoes.length === 0 ? (
              <div className="flex items-center justify-center gap-2.5 py-16">
                <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
                <span className="text-[13.5px] font-semibold text-slate-600">
                  Carregando solicitações...
                </span>
              </div>
            ) : (
              <ModernTable>
                <Thead>
                  <Tr>
                    <Th className="w-[17%] min-w-[200px]">Destino</Th>
                    <Th className="w-[12%] min-w-[140px]">Data/Hora</Th>
                    <Th className="w-[21%] min-w-[220px]">Motivo</Th>
                    <Th className="w-[16%] min-w-[190px]">Veículo</Th>
                    <Th className="w-[16%] min-w-[190px]">Funcionário</Th>
                    <Th className="w-[10%] min-w-[120px]">Status</Th>
                    <Th className="w-[8%] min-w-[130px]">Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {solicitacoesPaginadas.length === 0 ? (
                    <Tr>
                      <Td colSpan={7}>
                        <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
                          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                            <MapPin className="h-7 w-7 stroke-[1.8]" />
                          </div>
                          <div className="text-[14px] font-bold text-slate-700">
                            Nenhuma solicitação de viagem em aberto
                          </div>
                          <p className="max-w-sm text-[12.5px] font-medium leading-relaxed text-slate-500">
                            {searchTerm
                              ? 'Nenhum resultado para a busca atual. Tente outro termo.'
                              : 'Clique em "Nova Solicitação" para cadastrar a primeira.'}
                          </p>
                        </div>
                      </Td>
                    </Tr>
                  ) : (
                    solicitacoesPaginadas.map((s) => {
                      const vehicleFullText =
                        [s.vehicle?.brand || s.vehicle?.description, s.vehicle?.license_plate]
                          .filter(Boolean)
                          .join(' - Placa: ') || '-';
                      const employeeFullText =
                        [s.employee?.name, s.employee?.cpf || String(s.employee_id)]
                          .filter(Boolean)
                          .join(' - CPF: ') || '-';
                      return (
                        <Tr key={s.id}>
                          <Td>
                            <div className="min-w-0 w-full max-w-[260px] overflow-hidden">
                              <div className="flex items-start gap-2.5">
                                <div className="mt-0.5 shrink-0 flex h-9 w-9 min-w-[36px] min-h-[36px] items-center justify-center rounded-xl bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-200">
                                  <MapPin className="h-[18px] w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <span
                                    className="block max-w-[200px] truncate text-[13.5px] font-semibold leading-tight text-slate-900"
                                    title={s.destination || '-'}
                                  >
                                    {s.destination || '-'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div>
                              <div className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-700 tabular-nums">
                                <Calendar className="h-[15px] w-[15px] text-slate-400" />
                                {formatDate(s.start_date)}
                              </div>
                              <div className="mt-1 inline-flex items-center gap-1.5 text-[11.5px] font-medium text-slate-500 tabular-nums">
                                <Clock className="h-[13px] w-[13px] text-slate-400" />
                                {formatTime(s.start_date)}
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[360px] overflow-hidden">
                              <p
                                className="text-[13px] font-medium leading-snug text-slate-800 line-clamp-2"
                                title={s.purpose || '-'}
                              >
                                {s.purpose || '-'}
                              </p>
                            </div>
                          </Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[260px] overflow-hidden">
                              <div className="flex items-start gap-2.5">
                                <div className="mt-0.5 shrink-0 flex h-9 w-9 min-w-[36px] min-h-[36px] items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
                                  <Car className="h-[18px] w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <span
                                    className="block max-w-[200px] truncate text-[13.5px] font-semibold leading-tight text-slate-900"
                                    title={vehicleFullText}
                                  >
                                    {s.vehicle?.brand || s.vehicle?.description || '-'}
                                  </span>
                                  <div className="mt-0.5 text-[11.5px] font-medium leading-tight text-slate-500 inline-flex items-center gap-1">
                                    <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-slate-600 font-[11px] font-semibold tabular-nums tracking-wide">
                                      {s.vehicle?.license_plate || 'Sem placa'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[260px] overflow-hidden">
                              <div className="flex items-start gap-2.5">
                                <div className="mt-0.5 shrink-0 flex h-9 w-9 min-w-[36px] min-h-[36px] items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200">
                                  <User className="h-[18px] w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <span
                                    className="block max-w-[200px] truncate text-[13.5px] font-semibold leading-tight text-slate-900"
                                    title={employeeFullText}
                                  >
                                    {s.employee?.name || '-'}
                                  </span>
                                  <div className="mt-0.5 text-[11.5px] font-medium leading-tight text-slate-500 tabular-nums">
                                    {s.employee?.cpf || s.employee_id}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>{renderStatusBadge(s.status)}</Td>
                          <Td>
                            <div className="flex items-center justify-center gap-1.5">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => visualizarSolicitacao(s)}
                                title="Visualizar"
                                className="h-8 w-8 rounded-lg text-slate-500 hover:text-indigo-700 hover:bg-indigo-50 hover:ring-indigo-200"
                              >
                                <Eye className="h-4 w-4 stroke-[1.9]" />
                              </Button>
                              {s.status === 'pendente' && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleAprovar(s)}
                                    title="Aprovar"
                                    className="h-8 w-8 rounded-lg text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 hover:ring-emerald-200"
                                  >
                                    <CheckCircle2 className="h-4 w-4 stroke-[1.9]" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleRejeitar(s)}
                                    title="Rejeitar"
                                    className="h-8 w-8 rounded-lg text-rose-600 hover:text-rose-700 hover:bg-rose-50 hover:ring-rose-200"
                                  >
                                    <XCircle className="h-4 w-4 stroke-[1.9]" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </Td>
                        </Tr>
                      );
                    })
                  )}
                </Tbody>
              </ModernTable>
            )}

            {pageRange.total > 0 ? (
              <div className="mt-4 flex flex-col gap-3 border-t border-slate-200/80 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-[12.5px] font-medium leading-tight text-slate-500">
                  Mostrando{' '}
                  <span className="font-bold tabular-nums text-slate-800">{pageRange.start}</span>
                  {'–'}{' '}
                  <span className="font-bold tabular-nums text-slate-800">{pageRange.end}</span>
                  {' de '}
                  <span className="font-bold tabular-nums text-slate-800">{pageRange.total}</span>
                  {' solicitação(ões)'}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:ring-offset-1"
                    aria-label="Primeira página"
                  >
                    <ChevronsLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:ring-offset-1"
                    aria-label="Página anterior"
                  >
                    <ChevronLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <div className="inline-flex items-center gap-1 mx-1">
                    {getPageNumbers.map((n, idx) =>
                      n === '...' ? (
                        <span
                          key={`dots-${idx}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setCurrentPage(n)}
                          disabled={submitting}
                          className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:ring-offset-1 ${
                            n === currentPage
                              ? 'bg-indigo-600 text-white shadow-[0_4px_14px_-4px_rgba(79,70,229,0.55)]'
                              : 'border border-slate-200 bg-white text-slate-700 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700'
                          }`}
                        >
                          {n}
                        </button>
                      ),
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:ring-offset-1"
                    aria-label="Próxima página"
                  >
                    <ChevronRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:ring-offset-1"
                    aria-label="Última página"
                  >
                    <ChevronsRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                </div>
              </div>
            ) : null}
          </CardBody>
        </SectionCard>
      </div>

      {isCreateModalOpen && (
        <Modal
          onClose={closeCreateModal}
          maxWidthClass="max-w-2xl"
          title={
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 min-w-[40px] min-h-[40px] shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 via-blue-500 to-sky-600 text-white shadow-[0_6px_16px_-8px_rgba(79,70,229,0.65)]">
                <Route className="h-5 w-5 stroke-[1.9]" />
              </div>
              <div>
                <div className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">
                  Nova Solicitação de Viagem
                </div>
                <div className="mt-1 text-[12px] font-medium text-slate-500 leading-tight">
                  Selecione motorista e veículo disponíveis no seu setor
                </div>
              </div>
            </div>
          }
          footer={
            <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:items-center">
              <button
                type="button"
                onClick={closeCreateModal}
                disabled={submitting}
                className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-5 text-[13px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/30 disabled:pointer-events-none disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                type="submit"
                form="solicitacao-viagem-form"
                disabled={submitting}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-indigo-600 via-blue-600 to-sky-700 px-6 text-[13px] font-semibold text-white shadow-[0_4px_14px_-4px_rgba(79,70,229,0.55)] transition-all hover:shadow-[0_8px_22px_-6px_rgba(79,70,229,0.65)] focus:outline-none focus:ring-4 focus:ring-indigo-500/25 disabled:pointer-events-none disabled:opacity-60 disabled:shadow-none"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin stroke-[2]" /> Enviando...
                  </>
                ) : (
                  'Enviar Solicitação'
                )}
              </button>
            </div>
          }
        >
          <form
            id="solicitacao-viagem-form"
            onSubmit={submitCreateTrip}
            className="space-y-5"
            noValidate
          >
            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-emerald-500 to-teal-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-emerald-700">
                  Equipe e Veículo
                </h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="create-employee">
                      <span className="inline-flex items-center gap-1">
                        <User className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" />
                        Funcionário
                      </span>
                      <span className="text-rose-500 ml-0.5 font-bold leading-none">*</span>
                    </Label>
                    <Select
                      id="create-employee"
                      value={employeeId ?? ''}
                      onChange={(e) => setEmployeeId(e.target.value ? Number(e.target.value) : null)}
                      required
                    >
                      <option value="">Selecione um funcionário...</option>
                      {employees.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.name} - {emp.cpf}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="create-vehicle">
                      <span className="inline-flex items-center gap-1">
                        <Car className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" />
                        Veículo
                      </span>
                      <span className="text-rose-500 ml-0.5 font-bold leading-none">*</span>
                    </Label>
                    <Select
                      id="create-vehicle"
                      value={vehicleId ?? ''}
                      onChange={(e) => setVehicleId(e.target.value ? Number(e.target.value) : null)}
                      required
                    >
                      <option value="">Selecione um veículo...</option>
                      {vehicles.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.brand || v.description} - {v.license_plate}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-blue-500 to-indigo-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-blue-700">
                  Dados da Viagem
                </h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="create-start">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" />
                        Data/Hora início
                      </span>
                      <span className="text-rose-500 ml-0.5 font-bold leading-none">*</span>
                    </Label>
                    <Input
                      type="datetime-local"
                      id="create-start"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      required
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="create-end">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" />
                        Data/Hora fim
                        <span className="text-[11px] font-medium text-slate-400 ml-1">
                          (opcional)
                        </span>
                      </span>
                    </Label>
                    <Input
                      type="datetime-local"
                      id="create-end"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <Label htmlFor="create-destination">
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" />
                      Destino
                      <span className="text-[11px] font-medium text-slate-400 ml-1">
                        (opcional)
                      </span>
                    </span>
                  </Label>
                  <Input
                    type="text"
                    id="create-destination"
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    placeholder="Ex: Unidade Centro / Hospital Regional"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label htmlFor="create-purpose">
                    <span className="inline-flex items-center gap-1">
                      <FileText className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" />
                      Motivo
                      <span className="text-[11px] font-medium text-slate-400 ml-1">
                        (opcional)
                      </span>
                    </span>
                  </Label>
                  <Textarea
                    id="create-purpose"
                    value={purpose}
                    onChange={(e) => setPurpose(e.target.value)}
                    rows={3}
                    placeholder="Descreva o objetivo desta viagem..."
                  />
                </div>
              </div>
            </div>
          </form>
        </Modal>
      )}

      {isViewModalOpen && solicitacaoSelecionada && (
        <Modal
          onClose={closeViewModal}
          maxWidthClass="max-w-2xl"
          title={
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 min-w-[40px] min-h-[40px] shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 via-blue-500 to-sky-600 text-white shadow-[0_6px_16px_-8px_rgba(79,70,229,0.65)]">
                <Eye className="h-5 w-5 stroke-[1.9]" />
              </div>
              <div>
                <div className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">
                  Detalhes da Solicitação
                </div>
                <div className="mt-1 text-[12px] font-medium text-slate-500 leading-tight">
                  Visualize os dados completos da viagem
                </div>
              </div>
            </div>
          }
          footer={
            <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:items-center">
              <button
                type="button"
                onClick={closeViewModal}
                className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-5 text-[13px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/30"
              >
                Fechar
              </button>
              {solicitacaoSelecionada.status === 'pendente' && (
                <>
                  <Button
                    variant="success"
                    onClick={() => {
                      const trip = solicitacaoSelecionada;
                      closeViewModal();
                      handleAprovar(trip);
                    }}
                    className="h-11 px-5"
                  >
                    <CheckCircle2 className="h-4 w-4 stroke-[1.9]" />
                    Aprovar
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => {
                      const trip = solicitacaoSelecionada;
                      closeViewModal();
                      handleRejeitar(trip);
                    }}
                    className="h-11 px-5"
                  >
                    <XCircle className="h-4 w-4 stroke-[1.9]" />
                    Rejeitar
                  </Button>
                </>
              )}
            </div>
          }
        >
          <div className="space-y-5">
            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-blue-500 to-indigo-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-blue-700">
                  Dados da Viagem
                </h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">Destino</span>
                    <p className="text-sm text-slate-900 font-medium">
                      {solicitacaoSelecionada.destination || '-'}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">
                      Data e Hora de Início
                    </span>
                    <p className="text-sm text-slate-900 font-medium tabular-nums">
                      {formatDateTime(solicitacaoSelecionada.start_date)}
                    </p>
                  </div>
                  {solicitacaoSelecionada.end_date && (
                    <div className="flex flex-col gap-1.5 md:col-span-2">
                      <span className="text-xs font-semibold text-slate-500">
                        Previsão de Término
                      </span>
                      <p className="text-sm text-slate-900 font-medium tabular-nums">
                        {formatDateTime(solicitacaoSelecionada.end_date)}
                      </p>
                    </div>
                  )}
                  <div className="flex flex-col gap-1.5 md:col-span-2">
                    <span className="text-xs font-semibold text-slate-500">Motivo</span>
                    <div className="rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-slate-800 whitespace-pre-wrap min-h-[64px]">
                      {solicitacaoSelecionada.purpose || '-'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-emerald-500 to-teal-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-emerald-700">
                  Equipe e Veículo
                </h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">Veículo</span>
                    <p className="text-sm text-slate-900 font-medium">
                      {solicitacaoSelecionada.vehicle?.brand ||
                        solicitacaoSelecionada.vehicle?.description ||
                        '-'}{' '}
                      <span className="ml-1.5 inline-flex rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums tracking-wide text-slate-600">
                        {solicitacaoSelecionada.vehicle?.license_plate || 'Sem placa'}
                      </span>
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">Funcionário</span>
                    <p className="text-sm text-slate-900 font-medium">
                      {solicitacaoSelecionada.employee?.name ||
                        solicitacaoSelecionada.employee_id}
                    </p>
                    {solicitacaoSelecionada.employee?.cpf && (
                      <p className="text-[12px] text-slate-500 tabular-nums">
                        {solicitacaoSelecionada.employee.cpf}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5 md:col-span-2">
                    <span className="text-xs font-semibold text-slate-500">Status atual</span>
                    <div className="flex flex-wrap items-center gap-2">
                      {renderStatusBadge(solicitacaoSelecionada.status)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {solicitacaoSelecionada.status === 'rejeitada' &&
              solicitacaoSelecionada.cancellation_reason && (
                <div>
                  <div className="mb-2 flex items-center gap-2">
                    <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-rose-500 to-red-600" />
                    <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-rose-700">
                      Motivo da Rejeição
                    </h3>
                  </div>
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-sm text-rose-800 whitespace-pre-wrap">
                    {solicitacaoSelecionada.cancellation_reason}
                  </div>
                </div>
              )}
          </div>
        </Modal>
      )}

      <ConfirmModal
        isOpen={confirmData.isOpen}
        title={confirmData.title}
        description={confirmData.description}
        confirmVariant={confirmData.variant === 'danger' ? 'danger' : 'primary'}
        confirmLabel={confirmData.confirmText || 'Confirmar'}
        cancelLabel={confirmData.cancelText || 'Cancelar'}
        onCancel={() => setConfirmData((p) => ({ ...p, isOpen: false }))}
        onConfirm={() => {
          void confirmData.onConfirm?.();
        }}
      />
    </div>
  );
};

export default SolicitacaoViagem;
